import classNames from 'classnames';
import React, { useEffect, useState, useRef } from 'react';
import { Modal } from 'react-bootstrap';
import styles from './style.module.scss';
import { PlusIcon, ProjectNameIcon, StatusCheckIcon, EyeIcon, DownloadIcon, EditIcon } from 'assets';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import LayoutRl from './LayoutRl';
import GenericTable from 'shared/components/common/table/table';
import DashboardWrapper from 'shared/components/common/dashboard_wrapper/DashboardWrapper';
import Heading from 'shared/components/common/heading/Heading';
import CustomButton from 'shared/components/common/custom_button/CustomButton';
import AddClient from 'shared/modal/add_product/AddProduct';
import OverlayLoading from 'shared/screen_loader/overlay_loader/OverlayLoading';
import { GetAllRoughListingQuery } from 'shared/services/rough_listing_service/RoughListingService';
import moment from 'moment';
import { isRestrictActions, hasActionPermission } from 'shared/utils/Helper';
import { screenActionEnum, screenEnum } from 'shared/utils/Enum';
import { routeConstant } from 'shared/routes/RouteConstant';
import ExcelExport from 'shared/components/common/ExcelExport/ExcelExport';
import PoSearchBar from 'shared/components/common/searchbar/PoSearchBar';
import { roleEnum } from 'shared/utils/Enum';
import Select from 'react-select';
import { GetAllWarehouse } from 'shared/services/ware_house_service/WareHouseService';
import ModalImage from 'shared/components/common/custom_image_lightbox/ModalImage';
import { toastMessage } from 'shared/components/toast/Toast';

const getSkuSearchString = (item) => {
    if (!item) return '';
    const brandStr = typeof item.brand === 'object' ? item.brand?.name : item.brand;
    const compBrandStr = typeof item.compatiableBrand === 'object'
        ? item.compatiableBrand?.name
        : (typeof item.competebleBrand === 'object'
            ? item.competebleBrand?.name
            : (item.compatiableBrand || item.competebleBrand));
    const catStr = typeof item.category === 'object'
        ? item.category?.name
        : (typeof item.product_category === 'object'
            ? item.product_category?.name
            : (item.category || item.product_category));
    const subCatStr = typeof item.sub_category === 'object' ? item.sub_category?.name : item.sub_category;
    const colorStr = typeof item.color === 'object'
        ? (item.color?.name || item.color?.label || item.color?.color)
        : item.color;
    const prodStr = typeof item.product === 'object'
        ? (item.product?.name || item.product?.model)
        : item.product;

    const parts = [
        item.sku,
        item.parentChildSku,
        item.name,
        item.product_name,
        item.model,
        prodStr,
        brandStr,
        compBrandStr,
        catStr,
        subCatStr,
        colorStr,
        item.condition,
        item.status,
        item.rlOrderNumber || item.roughListing?.order_number,
        item.rlSubmittedBy || item.roughListing?.submitted_by?.name,
        item.rlStatus || item.roughListing?.status,
        item.batchNumber ? `batch ${item.batchNumber}` : '',
        item.badge ? `batch ${item.badge}` : '',
        item.price ? String(item.price) : '',
        item.quantity ? String(item.quantity) : ''
    ];

    return parts.filter(Boolean).join(' ').toLowerCase();
};

const getRLSearchString = (rl) => {
    if (!rl) return '';
    const whStr = typeof rl.warehouse === 'object' ? (rl.warehouse?.code || rl.warehouse?.name) : rl.warehouse;
    const subStr = typeof rl.submitted_by === 'object' ? (rl.submitted_by?.name || rl.submitted_by?.email) : rl.submitted_by;

    const parts = [
        rl.order_number,
        rl.status,
        subStr,
        whStr,
        rl.note
    ];

    if (Array.isArray(rl.order)) {
        for (let i = 0; i < rl.order.length; i++) {
            parts.push(getSkuSearchString(rl.order[i]));
        }
    }

    return parts.filter(Boolean).join(' ').toLowerCase();
};

const extractSearchTokens = (searchTerm) => {
    if (!searchTerm) return [];
    const regex = /"([^"]+)"|'([^']+)'|(\S+)/g;
    const tokens = [];
    let match;
    while ((match = regex.exec(searchTerm)) !== null) {
        const token = match[1] || match[2] || match[3];
        if (token && token.trim()) {
            tokens.push(token.trim().toLowerCase());
        }
    }
    return tokens;
};

const matchItemWithTokens = (searchString, tokens) => {
    if (!tokens || tokens.length === 0) return true;
    return tokens.every(token => searchString.includes(token));
};

export default function RoughListing() {
    const {
        theme: { theme },
        user
    } = useSelector((state) => state.root);
    const [searchMenu, setsearchMenu] = useState('');
    const [appliedSearchTerm, setAppliedSearchTerm] = useState('');
    const [clicksearch, setClickSearch] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const statusOptions = [
        { label: 'All Statuses', value: '' },
        { label: 'Approved', value: 'approved' },
        { label: 'Pending', value: 'pending' },
        { label: 'Draft', value: 'draft' },
        { label: 'Declined', value: 'declined' },
        { label: 'Existing', value: 'existing' }
    ];

    const [selectedStatus, setSelectedStatus] = useState(null);
    const [wareHouses, setWareHouses] = useState([]);
    const [selectedWarehouse, setSelectedWarehouse] = useState();

    // Expansion state per Rough Listing ID
    const [expandedRLs, setExpandedRLs] = useState({});
    // Expansion state per Batch Key (starts collapsed!)
    const [expandedBatches, setExpandedBatches] = useState({});

    // Debounce typing so user can type smoothly without any UI freeze
    useEffect(() => {
        const timer = setTimeout(() => {
            setAppliedSearchTerm(searchMenu);
        }, 250);
        return () => clearTimeout(timer);
    }, [searchMenu]);

    // View mode: 'tree' (hierarchical) or 'sku' (all individual SKUs)
    const [viewMode, setViewMode] = useState(() => {
        try {
            return localStorage.getItem('rough_listing_view_mode') || 'tree';
        } catch (e) {
            return 'tree';
        }
    });

    const handleViewModeChange = (mode) => {
        setViewMode(mode);
        try {
            localStorage.setItem('rough_listing_view_mode', mode);
        } catch (e) {}
    };

    const obj = {};
    if (clicksearch) {
        obj['search'] = clicksearch;
    }
    const queryObj = {};
    if (selectedWarehouse?.value) queryObj.warehouseId = selectedWarehouse.value;
    const queryString = new URLSearchParams(queryObj).toString();

    const { isLoading, data: PoList, isError, error, isSuccess } = GetAllRoughListingQuery(queryString);
    const { data: listWareHouse } = GetAllWarehouse();

    const filterData = (dataList, searchTerm, statusFilter) => {
        let baseList = Array.isArray(dataList)
            ? dataList
            : Array.isArray(dataList?.data)
                ? dataList.data
                : [];

        let filtered = [...baseList];

        if (statusFilter?.value) {
            const val = statusFilter.value.toLowerCase();
            filtered = filtered.filter((item) => {
                const itemStatus = (item?.status || '').toLowerCase();
                const hasMatchingOrderItem = (item?.order || []).some(
                    (o) => (o?.status || '').toLowerCase() === val
                );
                return itemStatus === val || hasMatchingOrderItem;
            });
        }

        if (searchTerm && searchTerm.trim().length > 0) {
            const tokens = extractSearchTokens(searchTerm);
            filtered = filtered.filter((item) => {
                const searchStr = getRLSearchString(item);
                return matchItemWithTokens(searchStr, tokens);
            });
        }

        return filtered;
    };

    const SearchBars = (customSearchTerm) => {
        const term = customSearchTerm !== undefined ? customSearchTerm : searchMenu;
        setAppliedSearchTerm(term);
        const filtered = filterData(PoList, term, selectedStatus);
        setSearchResults(filtered);
    };

    useEffect(() => {
        const wareHouseoptions = (listWareHouse || []).map((w) => ({
            ...w,
            label: w?.code,
            value: w?._id,
        }));
        setWareHouses(wareHouseoptions);

        if (!isSuccess || !PoList) return;
        const poArray = Array.isArray(PoList)
            ? PoList
            : Array.isArray(PoList.data)
                ? PoList.data
                : [];

        poArray.forEach(po => {
            const totalAmount = (po.order || []).reduce((acc, order) => {
                const price = Number(order.price) || 0;
                const qty = Number(order.quantity) || 0;
                return acc + price * qty;
            }, 0);

            po.amount = totalAmount;
        });

        if (PoList.data) {
            PoList.data = poArray;
        }

        const filtered = filterData(PoList, appliedSearchTerm, selectedStatus);
        setSearchResults(filtered);
    }, [PoList, isSuccess, selectedStatus, appliedSearchTerm]);

    const navigate = useNavigate();
    const [openProduct, setOpenProduct] = useState(false);
    const pdfContainerRef = useRef(null);
    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

    // Download format selection states
    const [showDownloadModal, setShowDownloadModal] = useState(false);
    const [selectedPO, setSelectedPO] = useState(null);

    const handleDownloadIconClick = (poData) => {
        setSelectedPO(poData);
        setShowDownloadModal(true);
    };

    const handleDownloadExcel = () => {
        if (!selectedPO) return;
        const poData = selectedPO;
        
        const dataForExcel = (poData.order || []).map(item => ({
            "Brand": item.brand?.name || '',
            "Compatible Brand": item.compatiableBrand?.name || '',
            "Category": item.product_category?.name || '',
            "Sub Category": item.sub_category?.name || '',
            "Model": item.product?.name || item.product?.model || '',
            "Color": item.color || '',
            "Quantity": item.quantity || 0,
            "Price": item.price || 0,
            "Total": (item.quantity || 0) * (item.price || 0)
        }));

        const worksheet = XLSX.utils.json_to_sheet(dataForExcel);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "PO Items");
        XLSX.writeFile(workbook, `RL_${poData.order_number || poData._id}.xlsx`);

        setShowDownloadModal(false);
    };

    const handleDownloadPDF = async () => {
        if (!selectedPO || !pdfContainerRef.current) return;
        setIsGeneratingPdf(true);
        try {
            const elementToCapture = pdfContainerRef.current;
            const canvas = await html2canvas(elementToCapture, { scale: 2, useCORS: true });
            const imgData = canvas.toDataURL('image/png');
            
            const pdf = new jsPDF('p', 'mm', 'a4');
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
            
            pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
            pdf.save(`RL_${selectedPO.order_number || selectedPO._id}.pdf`);
        } catch (err) {
            console.error("Error generating PDF", err);
        } finally {
            setIsGeneratingPdf(false);
            setShowDownloadModal(false);
        }
    };

    useEffect(() => {
        if (PoList) {
            setSearchResults(PoList);
        }
    }, [PoList]);

    const navigateToPath = () => {
        navigate(routeConstant.admin.roughListing.addrl.path);
    };

    const views = (data) => {
        navigate(routeConstant.admin.roughListing.viewrl.path, { state: { data } });
    };

    const closeProductHandler = () => setOpenProduct(false);

    const formatPrice = (price) => {
        const num = Number(price);
        if (isNaN(num)) return 'Rs 0.00';
        return `Rs ${num.toFixed(2)}`;
    };

    // Main Columns: Order#, Submitted By, Approved By, Status, Action
    const columns = React.useMemo(
        () => [
            {
                Header: (
                    <div className={classNames(`${theme}FontColor`)}>
                        <ProjectNameIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        Order#
                    </div>
                ),
                accessor: 'order_number',
                width: 350,
                minWidth: 280,
                Cell: ({ row }) => {
                    const rl = row.original;
                    const rlId = rl?._id || rl?.order_number;
                    const isExpanded = !!expandedRLs[rlId];
                    const orderItems = rl?.order || [];

                    // Group order items by batch to count batches
                    const batchSet = new Set(orderItems.map(item => item.badge || 1));
                    const batchCount = batchSet.size;

                    return (
                        <div style={{ display: 'flex', alignItems: 'center', width: '100%', whiteSpace: 'nowrap' }}>
                            <span
                                style={{
                                    marginRight: '10px',
                                    cursor: 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '22px',
                                    height: '22px',
                                    borderRadius: '5px',
                                    background: isExpanded ? '#3b82f6' : 'rgba(0,0,0,0.06)',
                                    color: isExpanded ? 'white' : '#64748b',
                                    fontSize: '11px',
                                    fontWeight: 'bold',
                                    transition: 'all 0.2s ease',
                                    flexShrink: 0
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setExpandedRLs(prev => ({
                                        ...prev,
                                        [rlId]: !prev[rlId]
                                    }));
                                }}
                                title={isExpanded ? 'Collapse Batches' : 'Expand Batches'}
                            >
                                {isExpanded ? '▼' : '▶'}
                            </span>
                            <span
                                style={{ fontWeight: '700', color: '#1e293b', cursor: 'pointer', fontSize: '14px', flexShrink: 0 }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (rl?.order_number) {
                                        navigator.clipboard.writeText(rl.order_number);
                                        toastMessage('success', `Copied ${rl.order_number}`);
                                    }
                                }}
                                title="Click to copy Order #"
                            >
                                {rl?.order_number}
                            </span>
                            {batchCount > 0 && (
                                <span className="badge bg-primary bg-opacity-10 text-primary ms-2" style={{ fontSize: '11px', borderRadius: '4px', fontWeight: '600', flexShrink: 0 }}>
                                    {batchCount} {batchCount === 1 ? 'Batch' : 'Batches'} ({orderItems.length})
                                </span>
                            )}
                        </div>
                    );
                }
            },
            {
                Header: (
                    <div className={classNames(`${theme}FontColor`)}>
                        <ProjectNameIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        Submitted By
                    </div>
                ),
                accessor: 'submitted_by',
                width: 220,
                minWidth: 160,
                Cell: ({ row }) => {
                    const submittedByName = row.original?.submitted_by?.name;
                    const issueDate = row.original?.created_at || row.original?.date;

                    return (
                        <div style={{ lineHeight: '1.3' }}>
                            <div style={{ fontWeight: '500' }}>{submittedByName || '-'}</div>
                            {issueDate && (
                                <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                                    {moment(issueDate).format('DD, MMM YYYY')}
                                </div>
                            )}
                        </div>
                    );
                }
            },
            {
                Header: (
                    <div className={classNames(`${theme}FontColor`)}>
                        <ProjectNameIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        Approved By
                    </div>
                ),
                accessor: 'approved_by',
                width: 220,
                minWidth: 160,
                Cell: ({ row }) => {
                    const rl = row.original;
                    const dbStatus = rl?.status;
                    const allReviewed = (rl?.order || []).length > 0 &&
                        (rl?.order || []).every(item =>
                            item.status === 'approved' || item.status === 'existing' || item.status === 'declined'
                        );
                    const isCompleted = (rl?.tasks_created && allReviewed) || dbStatus === 'completed';
                    const approvedByName = rl?.approved_by?.name;

                    if (!approvedByName && !isCompleted) return <div>-</div>;

                    const approvalDate = rl?.approved_at || rl?.updated_at || rl?.created_at;

                    return (
                        <div style={{ lineHeight: '1.3' }}>
                            <div style={{ fontWeight: '500' }}>{approvedByName || '-'}</div>
                            {isCompleted && approvalDate && (
                                <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                                    {moment(approvalDate).format('DD, MMM YYYY')}
                                </div>
                            )}
                        </div>
                    );
                }
            },
            {
                Header: (
                    <div className={classNames(`${theme}FontColor`)}>
                        <StatusCheckIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        Status
                    </div>
                ),
                accessor: 'status',
                width: 150,
                minWidth: 120,
                Cell: ({ row }) => {
                    const rl = row.original;
                    const dbStatus = rl?.status;

                    const allReviewed = (rl?.order || []).length > 0 &&
                        (rl?.order || []).every(item =>
                            item.status === 'approved' || item.status === 'existing' || item.status === 'declined'
                        );
                    const effectiveStatus = (rl?.tasks_created && allReviewed) || dbStatus === 'completed'
                        ? 'completed'
                        : dbStatus || 'draft';

                    return (
                        <span className={classNames(
                            'badge',
                            effectiveStatus === 'completed' ? 'bg-success text-white' : 'bg-warning text-dark'
                        )}>
                            {effectiveStatus}
                        </span>
                    );
                }
            },
            {
                Header: (
                    <div className={classNames(`${theme}FontColor`)}>
                        <StatusCheckIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        Action
                    </div>
                ),
                accessor: 'action',
                width: 140,
                minWidth: 110,
                Cell: ({ row }) => (
                    <div className="d-flex align-items-center gap-3">
                        <EyeIcon onClick={() => views(row?.original)} style={{ cursor: 'pointer' }} title="View RL" />
                        {(user?.user?.authorize === roleEnum.admin || hasActionPermission(screenEnum.rough_listing, screenActionEnum.update) || hasActionPermission(screenEnum.rough_listing, screenActionEnum.create)) && (
                            <EditIcon
                                onClick={() => navigate(routeConstant.admin.roughListing.addrl.path, { state: { editData: row.original } })}
                                style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                                title="Edit Rough Listing"
                            />
                        )}
                        <DownloadIcon onClick={() => handleDownloadIconClick(row?.original)} style={{ cursor: 'pointer', width: '20px', height: '20px' }} title="Download RL" className={`${theme}SvgIcon`} />
                    </div>
                )
            }
        ],
        [theme, expandedRLs, showDownloadModal, user]
    );

    const getColorDot = (colorName) => {
        if (!colorName || typeof colorName !== 'string') return null;
        const c = colorName.trim().toLowerCase();
        const map = {
            'black': '#000000',
            'white': '#ffffff',
            'red': '#ef4444',
            'blue': '#3b82f6',
            'green': '#22c55e',
            'yellow': '#eab308',
            'gold': '#d97706',
            'silver': '#94a3b8',
            'grey': '#64748b',
            'gray': '#64748b',
            'pink': '#ec4899',
            'purple': '#a855f7',
            'orange': '#f97316',
            'brown': '#78350f',
            'rose gold': '#b76e79',
            'navy': '#1e3a8a',
            'cyan': '#06b6d4',
            'transparent': 'transparent',
            'clear': 'transparent'
        };
        return map[c] || null;
    };

    // Inner sub-component rendered directly beneath the expanded row
    const renderRowSubComponent = ({ row }) => {
        const rl = row.original;
        const rlId = rl?._id || rl?.order_number;
        if (!expandedRLs[rlId]) return null;

        const orderItems = rl?.order || [];
        if (orderItems.length === 0) {
            return (
                <tr className="w-100">
                    <td colSpan={5} className="p-3 text-center text-muted" style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                        No items found in this rough listing.
                    </td>
                </tr>
            );
        }

        // Group items by batch
        const batchMap = {};
        orderItems.forEach((item) => {
            const b = item.badge || 1;
            if (!batchMap[b]) {
                batchMap[b] = [];
            }
            batchMap[b].push(item);
        });

        const batchNums = Object.keys(batchMap).map(Number).sort((a, b) => a - b);

        return (
            <tr className="w-100" style={{ background: '#f8fafc', borderBottom: '2px solid #cbd5e1' }}>
                <td colSpan={5} className="p-3">
                    <div className="d-flex flex-column gap-3 w-100">
                        {batchNums.map((bNum) => {
                            const rawItems = batchMap[bNum];
                            const parentItems = [];
                            const grandItemsMap = {};
                            const otherItems = [];

                            rawItems.forEach(item => {
                                const sku = item.sku ? item.sku.trim() : "";
                                const isGrand = /^wt\d{6}[a-z][0-9]$/i.test(sku) || item.parentChildSku;
                                if (isGrand) {
                                    const parentKey = (item.parentChildSku || sku.slice(0, -1)).toLowerCase();
                                    if (!grandItemsMap[parentKey]) grandItemsMap[parentKey] = [];
                                    grandItemsMap[parentKey].push(item);
                                } else if (sku && /^wt\d{6}[a-z]$/i.test(sku)) {
                                    parentItems.push(item);
                                } else {
                                    otherItems.push(item);
                                }
                            });

                            parentItems.sort((a, b) => (a.sku || "").localeCompare(b.sku || ""));

                            const bItems = [];
                            parentItems.forEach(parent => {
                                bItems.push(parent);
                                const parentKey = (parent.sku || "").toLowerCase();
                                const grandChildren = grandItemsMap[parentKey] || [];
                                grandChildren.sort((a, b) => (a.sku || "").localeCompare(b.sku || ""));
                                bItems.push(...grandChildren);
                                delete grandItemsMap[parentKey];
                            });

                            Object.values(grandItemsMap).forEach(remainingList => {
                                bItems.push(...remainingList);
                            });
                            bItems.push(...otherItems);

                            const firstSkuItem = bItems.find(it => it.sku && it.sku !== 'N/A');
                            const parentSku = firstSkuItem ? firstSkuItem.sku : (bItems[0]?.sku || 'N/A');
                            const bKey = `${rlId}_b_${bNum}`;
                            const isBatchOpen = !!expandedBatches[bKey];

                            return (
                                <div
                                    key={bNum}
                                    style={{
                                        background: '#ffffff',
                                        borderRadius: '8px',
                                        border: '1px solid #e2e8f0',
                                        overflow: 'hidden',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                                    }}
                                >
                                    {/* Batch Header (Clickable to expand/collapse items) */}
                                    <div
                                        className="d-flex align-items-center justify-content-between px-3 py-2"
                                        style={{ background: '#f1f5f9', borderBottom: isBatchOpen ? '1px solid #e2e8f0' : 'none', cursor: 'pointer' }}
                                        onClick={() => {
                                            setExpandedBatches(prev => ({
                                                ...prev,
                                                [bKey]: !prev[bKey]
                                            }));
                                        }}
                                        title={isBatchOpen ? 'Click to collapse batch items' : 'Click to expand batch items'}
                                    >
                                        <div className="d-flex align-items-center gap-3">
                                            <span
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    width: '22px',
                                                    height: '22px',
                                                    borderRadius: '4px',
                                                    background: isBatchOpen ? '#6366f1' : 'rgba(99,102,241,0.12)',
                                                    color: isBatchOpen ? 'white' : '#6366f1',
                                                    fontSize: '11px',
                                                    fontWeight: 'bold',
                                                    flexShrink: 0
                                                }}
                                            >
                                                {isBatchOpen ? '▼' : '▶'}
                                            </span>

                                            {/* Batch Representative Image */}
                                            {(() => {
                                                const batchImages = bItems.flatMap(it => [
                                                    ...(Array.isArray(it?.raw_images) ? it.raw_images : (it?.raw_images ? [it.raw_images] : [])),
                                                    it?.image,
                                                    it?.sub_category?.image
                                                ]).filter(Boolean);

                                                if (batchImages.length > 0) {
                                                    return (
                                                        <div style={{ width: '40px', height: '40px', flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                                                            <ModalImage
                                                                small={batchImages[0]}
                                                                large={batchImages[0]}
                                                                imageArray={batchImages}
                                                                alt={`Batch ${bNum}`}
                                                                style={{
                                                                    width: '40px',
                                                                    height: '40px',
                                                                    borderRadius: '6px',
                                                                    objectFit: 'cover',
                                                                    border: '1px solid #cbd5e1',
                                                                    cursor: 'pointer',
                                                                    boxShadow: '0 2px 4px rgba(0,0,0,0.06)'
                                                                }}
                                                            />
                                                        </div>
                                                    );
                                                }
                                                return null;
                                            })()}

                                            <div className="d-flex align-items-center gap-2 flex-wrap">
                                                <span style={{ fontWeight: '700', fontSize: '14px', color: '#1e293b' }}>
                                                    Batch {bNum}
                                                </span>
                                                {parentSku && parentSku !== 'N/A' && (
                                                    <span
                                                        className="badge"
                                                        style={{
                                                            background: '#eff6ff',
                                                            color: '#2563eb',
                                                            border: '1px solid #bfdbfe',
                                                            cursor: 'pointer',
                                                            fontSize: '11px',
                                                            fontWeight: '600',
                                                            padding: '4px 8px'
                                                        }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            navigator.clipboard.writeText(parentSku);
                                                            toastMessage('success', `SKU Copied: ${parentSku}`);
                                                        }}
                                                        title="Click to copy Batch SKU"
                                                    >
                                                        📋 SKU: {parentSku}
                                                    </span>
                                                )}
                                                <span className="text-muted" style={{ fontSize: '12px' }}>
                                                    ({bItems.length} item{bItems.length !== 1 ? 's' : ''})
                                                </span>
                                            </div>
                                        </div>

                                        <div className="text-muted" style={{ fontSize: '12px', fontWeight: '500' }}>
                                            {isBatchOpen ? '▲ Collapse' : '▼ Expand Items'}
                                        </div>
                                    </div>

                                    {/* Batch Items Table with Headings (only shown when batch is opened!) */}
                                    {isBatchOpen && (
                                        <div style={{ overflowX: 'auto' }}>
                                            <table className="table table-hover mb-0 align-middle" style={{ fontSize: '12px', minWidth: '980px' }}>
                                                <thead style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                                    <tr style={{ color: '#475569', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase' }}>
                                                        <th style={{ width: '100px', padding: '10px 12px' }} className="text-center">Images</th>
                                                        <th style={{ width: '130px', padding: '10px 12px' }}>SKU</th>
                                                        <th style={{ width: '120px', padding: '10px 12px' }}>Brand</th>
                                                        <th style={{ width: '130px', padding: '10px 12px' }}>Compatible</th>
                                                        <th style={{ width: '140px', padding: '10px 12px' }}>Model</th>
                                                        <th style={{ width: '130px', padding: '10px 12px' }}>Sub Category</th>
                                                        <th style={{ width: '130px', padding: '10px 12px' }}>Category</th>
                                                        <th style={{ width: '110px', padding: '10px 12px' }}>Color</th>
                                                        <th style={{ width: '65px', padding: '10px 12px' }} className="text-center">Qty</th>
                                                        <th style={{ width: '90px', padding: '10px 12px' }} className="text-center">Price</th>
                                                        <th style={{ width: '90px', padding: '10px 12px' }} className="text-center">Total</th>
                                                        <th style={{ width: '95px', padding: '10px 12px' }} className="text-center">Status</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {bItems.map((item, idx) => {
                                                        const rawImages = Array.isArray(item?.raw_images) ? item.raw_images : (item?.raw_images ? [item.raw_images] : []);
                                                        const subcatImg = item?.sub_category?.image || item?.sub_category_id?.image;
                                                        const itemImg = item?.image || item?.product_image;
                                                        const allItemImages = [...rawImages, subcatImg, itemImg].filter(Boolean);

                                                        const itemSku = item?.sku || 'N/A';
                                                        const qty = Number(item?.quantity) || 0;
                                                        const price = Number(item?.price) || 0;
                                                        const total = qty * price;
                                                        const status = item?.status || 'pending';
                                                        const colorVal = typeof item?.color === 'object' ? item?.color?.color || item?.color?.label || '' : (item?.color || '');
                                                        const colorDot = getColorDot(colorVal);

                                                        const statusBadgeClass = status === 'approved'
                                                            ? 'bg-success text-white'
                                                            : status === 'declined'
                                                                ? 'bg-danger text-white'
                                                                : status === 'existing'
                                                                    ? 'bg-info text-dark'
                                                                    : 'bg-warning text-dark';

                                                        const isGrandSku = itemSku && /^wt\d{6}[a-z][0-9]$/i.test(itemSku);

                                                        return (
                                                            <tr 
                                                                key={idx} 
                                                                style={{ 
                                                                    borderBottom: '1px solid #f1f5f9',
                                                                    background: isGrandSku ? '#f8fafc' : '#ffffff',
                                                                    borderLeft: isGrandSku ? '3px solid #0284c7' : '3px solid transparent'
                                                                }}
                                                            >
                                                                <td className="text-center" style={{ padding: '8px 12px' }}>
                                                                    {allItemImages.length > 0 ? (
                                                                        <div style={{ display: 'flex', gap: '4px', alignItems: 'center', justifyContent: 'center' }}>
                                                                            {allItemImages.slice(0, 2).map((url, imgIdx) => (
                                                                                <ModalImage
                                                                                    key={imgIdx}
                                                                                    small={url}
                                                                                    large={url}
                                                                                    imageArray={allItemImages}
                                                                                    currentIndex={imgIdx}
                                                                                    alt={`${itemSku}-${imgIdx}`}
                                                                                    style={{
                                                                                        width: '36px',
                                                                                        height: '36px',
                                                                                        borderRadius: '5px',
                                                                                        objectFit: 'cover',
                                                                                        border: '1px solid #e2e8f0',
                                                                                        cursor: 'pointer'
                                                                                    }}
                                                                                />
                                                                            ))}
                                                                            {allItemImages.length > 2 && (
                                                                                <span
                                                                                    style={{
                                                                                        fontSize: '10px',
                                                                                        fontWeight: '700',
                                                                                        color: '#2563eb',
                                                                                        background: '#eff6ff',
                                                                                        border: '1px solid #bfdbfe',
                                                                                        borderRadius: '4px',
                                                                                        padding: '2px 4px'
                                                                                    }}
                                                                                >
                                                                                    +{allItemImages.length - 2}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    ) : (
                                                                        <div
                                                                            style={{
                                                                            width: '36px',
                                                                            height: '36px',
                                                                            borderRadius: '5px',
                                                                            background: '#f1f5f9',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                            margin: '0 auto',
                                                                            fontSize: '12px',
                                                                            color: '#94a3b8'
                                                                        }}
                                                                    >
                                                                        🖼️
                                                                    </div>
                                                                )}
                                                            </td>
                                                            <td style={{ padding: '8px 12px' }}>
                                                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', marginLeft: isGrandSku ? '10px' : '0px', whiteSpace: 'nowrap' }}>
                                                                    {isGrandSku && (
                                                                        <span
                                                                            style={{
                                                                                color: '#0284c7',
                                                                                fontSize: '11px',
                                                                                fontWeight: '700',
                                                                                background: '#e0f2fe',
                                                                                border: '1px solid #bae6fd',
                                                                                borderRadius: '4px',
                                                                                padding: '1px 5px',
                                                                                whiteSpace: 'nowrap',
                                                                                flexShrink: 0
                                                                            }}
                                                                            title="Grand Sub-SKU"
                                                                        >
                                                                            ↳ Sub
                                                                        </span>
                                                                    )}
                                                                    <span
                                                                        style={{
                                                                            fontWeight: '600',
                                                                            color: isGrandSku ? '#166534' : '#2563eb',
                                                                            background: isGrandSku ? '#f0fdf4' : 'transparent',
                                                                            border: isGrandSku ? '1px solid #86efac' : 'none',
                                                                            padding: isGrandSku ? '2px 7px' : '0',
                                                                            borderRadius: isGrandSku ? '5px' : '0',
                                                                            cursor: 'pointer',
                                                                            display: 'inline-block',
                                                                            whiteSpace: 'nowrap',
                                                                            flexShrink: 0
                                                                        }}
                                                                        onClick={() => {
                                                                            if (itemSku && itemSku !== 'N/A') {
                                                                                navigator.clipboard.writeText(itemSku);
                                                                                toastMessage('success', `SKU Copied: ${itemSku}`);
                                                                            }
                                                                        }}
                                                                        title="Click to copy SKU"
                                                                    >
                                                                        {itemSku}
                                                                    </span>
                                                                </div>
                                                            </td>
                                                            <td style={{ padding: '8px 12px', fontWeight: '500' }}>
                                                                {item?.brand?.name || item?.brand || '-'}
                                                            </td>
                                                            <td style={{ padding: '8px 12px', color: '#64748b' }}>
                                                                {item?.compatiableBrand?.name || item?.compatiableBrand || item?.competebleBrand?.name || '-'}
                                                            </td>
                                                            <td style={{ padding: '8px 12px', fontWeight: '600', color: '#0f172a' }}>
                                                                {item?.product?.name || item?.product?.model || item?.product || '-'}
                                                            </td>
                                                            <td style={{ padding: '8px 12px', color: '#0284c7' }}>
                                                                {item?.sub_category?.name || item?.sub_category || '-'}
                                                            </td>
                                                            <td style={{ padding: '8px 12px', color: '#64748b' }}>
                                                                {item?.product_category?.name || item?.product_category || '-'}
                                                            </td>
                                                            <td style={{ padding: '8px 12px' }}>
                                                                {colorVal ? (
                                                                    <div className="d-flex align-items-center gap-1">
                                                                        {colorDot !== null && (
                                                                            <span
                                                                                style={{
                                                                                    width: '12px',
                                                                                    height: '12px',
                                                                                    borderRadius: '50%',
                                                                                    backgroundColor: colorDot,
                                                                                    border: '1px solid #cbd5e1',
                                                                                    display: 'inline-block',
                                                                                    flexShrink: 0
                                                                                }}
                                                                            />
                                                                        )}
                                                                        <span className="badge bg-light text-dark border" style={{ fontSize: '11px' }}>
                                                                            {colorVal}
                                                                        </span>
                                                                    </div>
                                                                ) : '-'}
                                                            </td>
                                                            <td className="text-center" style={{ padding: '8px 12px', fontWeight: '600' }}>
                                                                {qty}
                                                            </td>
                                                            <td className="text-center" style={{ padding: '8px 12px' }}>
                                                                {formatPrice(price)}
                                                            </td>
                                                            <td className="text-center" style={{ padding: '8px 12px', fontWeight: '600' }}>
                                                                {formatPrice(total)}
                                                            </td>
                                                            <td className="text-center" style={{ padding: '8px 12px' }}>
                                                                <span className={classNames('badge', statusBadgeClass)} style={{ fontSize: '11px', textTransform: 'capitalize' }}>
                                                                    {status}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </td>
        </tr>
    );
};

    // Flat SKU data for the All SKUs view
    const skuData = React.useMemo(() => {
        const list = searchResults || [];
        const flatList = [];

        list.forEach((rl) => {
            const orderItems = rl?.order || [];
            if (orderItems.length === 0) return;

            // Group items by batch, matching tree batch ordering
            const batchMap = {};
            orderItems.forEach((item) => {
                const b = item.badge || 1;
                if (!batchMap[b]) {
                    batchMap[b] = [];
                }
                batchMap[b].push(item);
            });

            const batchNums = Object.keys(batchMap).map(Number).sort((a, b) => a - b);

            batchNums.forEach((bNum) => {
                const rawItems = batchMap[bNum];
                const parentItems = [];
                const grandItemsMap = {};
                const otherItems = [];

                rawItems.forEach(item => {
                    const sku = item.sku ? item.sku.trim() : "";
                    const isGrand = /^wt\d{6}[a-z][0-9]$/i.test(sku) || item.parentChildSku;
                    if (isGrand) {
                        const parentKey = (item.parentChildSku || sku.slice(0, -1)).toLowerCase();
                        if (!grandItemsMap[parentKey]) grandItemsMap[parentKey] = [];
                        grandItemsMap[parentKey].push(item);
                    } else if (sku && /^wt\d{6}[a-z]$/i.test(sku)) {
                        parentItems.push(item);
                    } else {
                        otherItems.push(item);
                    }
                });

                parentItems.sort((a, b) => (a.sku || "").localeCompare(b.sku || ""));

                const sortedBatchItems = [];
                parentItems.forEach(parent => {
                    sortedBatchItems.push(parent);
                    const parentKey = (parent.sku || "").toLowerCase();
                    const grandChildren = grandItemsMap[parentKey] || [];
                    grandChildren.sort((a, b) => (a.sku || "").localeCompare(b.sku || ""));
                    sortedBatchItems.push(...grandChildren);
                    delete grandItemsMap[parentKey];
                });

                Object.values(grandItemsMap).forEach(remainingList => {
                    sortedBatchItems.push(...remainingList);
                });
                sortedBatchItems.push(...otherItems);

                sortedBatchItems.forEach((item, itemIdx) => {
                    const itemSku = item?.sku ? item.sku.trim() : '';
                    flatList.push({
                        ...item,
                        _id: item._id || `${rl._id || rl.order_number}_b${bNum}_${itemIdx}`,
                        roughListing: rl,
                        rlOrderNumber: rl.order_number || '-',
                        rlSubmittedBy: rl.submitted_by?.name || '-',
                        rlDate: rl.created_at || rl.date,
                        rlStatus: rl.status || 'draft',
                        batchNumber: bNum,
                        sku: itemSku || item.sku || 'N/A'
                    });
                });
            });
        });

        let filtered = flatList;
        if (appliedSearchTerm && appliedSearchTerm.trim().length > 0) {
            const tokens = extractSearchTokens(appliedSearchTerm);
            filtered = filtered.filter(item => {
                const searchStr = getSkuSearchString(item);
                return matchItemWithTokens(searchStr, tokens);
            });
        }

        if (selectedStatus?.value) {
            const val = selectedStatus.value.toLowerCase();
            filtered = filtered.filter(item => {
                const itemStatus = (item?.status || 'pending').toLowerCase();
                const rlStatus = (item?.rlStatus || '').toLowerCase();
                return itemStatus === val || rlStatus === val;
            });
        }

        return filtered;
    }, [searchResults, appliedSearchTerm, selectedStatus]);

    // Columns for the All SKUs view
    const skuColumns = React.useMemo(
        () => [
            {
                Header: (
                    <div className={classNames(`${theme}FontColor`)}>
                        <ProjectNameIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        Images
                    </div>
                ),
                accessor: 'images',
                id: 'images',
                width: 95,
                minWidth: 80,
                disableSortBy: true,
                Cell: ({ row }) => {
                    const item = row.original;
                    const rawImages = Array.isArray(item?.raw_images) ? item.raw_images : (item?.raw_images ? [item.raw_images] : []);
                    const subcatImg = item?.sub_category?.image || item?.sub_category_id?.image;
                    const itemImg = item?.image || item?.product_image;
                    const allItemImages = [...rawImages, subcatImg, itemImg].filter(Boolean);
                    const itemSku = item?.sku || 'N/A';

                    if (allItemImages.length > 0) {
                        return (
                            <div style={{ display: 'flex', gap: '4px', alignItems: 'center', justifyContent: 'center' }}>
                                {allItemImages.slice(0, 2).map((url, imgIdx) => (
                                    <ModalImage
                                        key={imgIdx}
                                        small={url}
                                        large={url}
                                        imageArray={allItemImages}
                                        currentIndex={imgIdx}
                                        alt={`${itemSku}-${imgIdx}`}
                                        style={{
                                            width: '36px',
                                            height: '36px',
                                            borderRadius: '5px',
                                            objectFit: 'cover',
                                            border: '1px solid #e2e8f0',
                                            cursor: 'pointer'
                                        }}
                                    />
                                ))}
                                {allItemImages.length > 2 && (
                                    <span
                                        style={{
                                            fontSize: '10px',
                                            fontWeight: '700',
                                            color: '#2563eb',
                                            background: '#eff6ff',
                                            border: '1px solid #bfdbfe',
                                            borderRadius: '4px',
                                            padding: '2px 4px'
                                        }}
                                    >
                                        +{allItemImages.length - 2}
                                    </span>
                                )}
                            </div>
                        );
                    }
                    return (
                        <div
                            style={{
                                width: '36px',
                                height: '36px',
                                borderRadius: '5px',
                                background: '#f1f5f9',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto',
                                fontSize: '12px',
                                color: '#94a3b8'
                            }}
                        >
                            🖼️
                        </div>
                    );
                }
            },
            {
                Header: (
                    <div className={classNames(`${theme}FontColor`)}>
                        <ProjectNameIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        SKU
                    </div>
                ),
                accessor: 'sku',
                id: 'sku',
                width: 140,
                minWidth: 120,
                Cell: ({ row }) => {
                    const item = row.original;
                    const itemSku = item?.sku || 'N/A';
                    const isGrandSku = itemSku && /^wt\d{6}[a-z][0-9]$/i.test(itemSku);

                    return (
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
                            {isGrandSku && (
                                <span
                                    style={{
                                        color: '#0284c7',
                                        fontSize: '10px',
                                        fontWeight: '700',
                                        background: '#e0f2fe',
                                        border: '1px solid #bae6fd',
                                        borderRadius: '4px',
                                        padding: '1px 5px',
                                        whiteSpace: 'nowrap',
                                        flexShrink: 0
                                    }}
                                    title="Grand Sub-SKU"
                                >
                                    ↳ Sub
                                </span>
                            )}
                            <span
                                style={{
                                    fontWeight: '600',
                                    color: isGrandSku ? '#166534' : '#2563eb',
                                    background: isGrandSku ? '#f0fdf4' : 'transparent',
                                    border: isGrandSku ? '1px solid #86efac' : 'none',
                                    padding: isGrandSku ? '2px 7px' : '0',
                                    borderRadius: isGrandSku ? '5px' : '0',
                                    cursor: 'pointer',
                                    display: 'inline-block',
                                    whiteSpace: 'nowrap',
                                    flexShrink: 0
                                }}
                                onClick={() => {
                                    if (itemSku && itemSku !== 'N/A') {
                                        navigator.clipboard.writeText(itemSku);
                                        toastMessage('success', `SKU Copied: ${itemSku}`);
                                    }
                                }}
                                title="Click to copy SKU"
                            >
                                {itemSku}
                            </span>
                        </div>
                    );
                }
            },
            {
                Header: (
                    <div className={classNames(`${theme}FontColor`)}>
                        <ProjectNameIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        RL Ref
                    </div>
                ),
                accessor: 'rlOrderNumber',
                id: 'rlOrderNumber',
                width: 160,
                minWidth: 130,
                Cell: ({ row }) => {
                    const { roughListing, rlOrderNumber, batchNumber } = row.original;
                    const submittedName = roughListing?.submitted_by?.name;
                    const date = roughListing?.created_at || roughListing?.date;

                    return (
                        <div style={{ lineHeight: '1.25' }}>
                            <div className="d-flex align-items-center gap-1">
                                <span
                                    style={{
                                        fontWeight: '700',
                                        color: '#1e293b',
                                        cursor: 'pointer',
                                        fontSize: '12px'
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (rlOrderNumber && rlOrderNumber !== '-') {
                                            navigator.clipboard.writeText(rlOrderNumber);
                                            toastMessage('success', `Copied ${rlOrderNumber}`);
                                        }
                                    }}
                                    title="Click to copy RL Order #"
                                >
                                    {rlOrderNumber}
                                </span>
                                {batchNumber !== undefined && (
                                    <span
                                        className="badge bg-primary bg-opacity-10 text-primary"
                                        style={{ fontSize: '10px', padding: '2px 5px', fontWeight: '600', borderRadius: '4px' }}
                                        title={`Batch ${batchNumber}`}
                                    >
                                        B{batchNumber}
                                    </span>
                                )}
                            </div>
                            {submittedName && (
                                <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '140px' }}>
                                    {submittedName} {date ? `• ${moment(date).format('DD MMM')}` : ''}
                                </div>
                            )}
                        </div>
                    );
                }
            },
            {
                Header: (
                    <div className={classNames(`${theme}FontColor`)}>
                        <ProjectNameIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        Brand
                    </div>
                ),
                accessor: (row) => row?.brand?.name || row?.brand || '',
                id: 'brand',
                width: 110,
                minWidth: 90,
                Cell: ({ value }) => <span style={{ fontWeight: '500' }}>{value || '-'}</span>
            },
            {
                Header: (
                    <div className={classNames(`${theme}FontColor`)}>
                        <ProjectNameIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        Compatible
                    </div>
                ),
                accessor: (row) => row?.compatiableBrand?.name || row?.compatiableBrand || row?.competebleBrand?.name || '',
                id: 'compatible',
                width: 110,
                minWidth: 90,
                Cell: ({ value }) => <span style={{ color: '#64748b' }}>{value || '-'}</span>
            },
            {
                Header: (
                    <div className={classNames(`${theme}FontColor`)}>
                        <ProjectNameIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        Model
                    </div>
                ),
                accessor: (row) => row?.product?.name || row?.product?.model || row?.product || '',
                id: 'model',
                width: 130,
                minWidth: 100,
                Cell: ({ value }) => <span style={{ fontWeight: '600', color: '#0f172a' }}>{value || '-'}</span>
            },
            {
                Header: (
                    <div className={classNames(`${theme}FontColor`)}>
                        <ProjectNameIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        Sub Category
                    </div>
                ),
                accessor: (row) => row?.sub_category?.name || row?.sub_category || '',
                id: 'sub_category',
                width: 120,
                minWidth: 90,
                Cell: ({ value }) => <span style={{ color: '#0284c7' }}>{value || '-'}</span>
            },
            {
                Header: (
                    <div className={classNames(`${theme}FontColor`)}>
                        <ProjectNameIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        Category
                    </div>
                ),
                accessor: (row) => row?.product_category?.name || row?.product_category || '',
                id: 'category',
                width: 120,
                minWidth: 90,
                Cell: ({ value }) => <span style={{ color: '#64748b' }}>{value || '-'}</span>
            },
            {
                Header: (
                    <div className={classNames(`${theme}FontColor`)}>
                        <ProjectNameIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        Color
                    </div>
                ),
                accessor: (row) => typeof row?.color === 'object' ? row?.color?.color || row?.color?.label || '' : (row?.color || ''),
                id: 'color',
                width: 100,
                minWidth: 80,
                Cell: ({ value: colorVal }) => {
                    if (!colorVal) return <span>-</span>;
                    const colorDot = getColorDot(colorVal);
                    return (
                        <div className="d-flex align-items-center gap-1">
                            {colorDot !== null && (
                                <span
                                    style={{
                                        width: '12px',
                                        height: '12px',
                                        borderRadius: '50%',
                                        backgroundColor: colorDot,
                                        border: '1px solid #cbd5e1',
                                        display: 'inline-block',
                                        flexShrink: 0
                                    }}
                                />
                            )}
                            <span className="badge bg-light text-dark border" style={{ fontSize: '11px' }}>
                                {colorVal}
                            </span>
                        </div>
                    );
                }
            },
            {
                Header: (
                    <div className={classNames(`${theme}FontColor`)}>
                        <ProjectNameIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        Submitted By
                    </div>
                ),
                accessor: 'submitted_by',
                id: 'submitted_by',
                width: 170,
                minWidth: 140,
                Cell: ({ row }) => {
                    const rl = row.original?.roughListing;
                    const submittedByName = rl?.submitted_by?.name || row.original?.rlSubmittedBy;
                    const issueDate = rl?.created_at || rl?.date || row.original?.rlDate;

                    return (
                        <div style={{ lineHeight: '1.3' }}>
                            <div style={{ fontWeight: '500' }}>{submittedByName || '-'}</div>
                            {issueDate && (
                                <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                                    {moment(issueDate).format('DD, MMM YYYY')}
                                </div>
                            )}
                        </div>
                    );
                }
            },
            {
                Header: (
                    <div className={classNames(`${theme}FontColor`)}>
                        <ProjectNameIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        Approved By
                    </div>
                ),
                accessor: 'approved_by',
                id: 'approved_by',
                width: 170,
                minWidth: 140,
                Cell: ({ row }) => {
                    const rl = row.original?.roughListing;
                    const dbStatus = rl?.status;
                    const allReviewed = (rl?.order || []).length > 0 &&
                        (rl?.order || []).every(item =>
                            item.status === 'approved' || item.status === 'existing' || item.status === 'declined'
                        );
                    const isCompleted = (rl?.tasks_created && allReviewed) || dbStatus === 'completed';
                    const approvedByName = rl?.approved_by?.name;

                    if (!approvedByName && !isCompleted) return <div>-</div>;

                    const approvalDate = rl?.approved_at || rl?.updated_at || rl?.created_at;

                    return (
                        <div style={{ lineHeight: '1.3' }}>
                            <div style={{ fontWeight: '500' }}>{approvedByName || '-'}</div>
                            {isCompleted && approvalDate && (
                                <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                                    {moment(approvalDate).format('DD, MMM YYYY')}
                                </div>
                            )}
                        </div>
                    );
                }
            },
            {
                Header: (
                    <div className={classNames(`${theme}FontColor`)}>
                        <StatusCheckIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        Status
                    </div>
                ),
                accessor: 'status',
                id: 'status',
                width: 95,
                minWidth: 80,
                Cell: ({ value }) => {
                    const status = value || 'pending';
                    const statusBadgeClass = status === 'approved'
                        ? 'bg-success text-white'
                        : status === 'declined'
                            ? 'bg-danger text-white'
                            : status === 'existing'
                                ? 'bg-info text-dark'
                                : 'bg-warning text-dark';
                    return (
                        <div className="text-center">
                            <span className={classNames('badge', statusBadgeClass)} style={{ fontSize: '11px', textTransform: 'capitalize' }}>
                                {status}
                            </span>
                        </div>
                    );
                }
            },
            {
                Header: (
                    <div className={classNames(`${theme}FontColor`)}>
                        <StatusCheckIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        Action
                    </div>
                ),
                accessor: 'action',
                id: 'action',
                width: 120,
                minWidth: 100,
                disableSortBy: true,
                Cell: ({ row }) => {
                    const rl = row.original?.roughListing;
                    if (!rl) return null;
                    return (
                        <div className="d-flex align-items-center gap-3">
                            <EyeIcon onClick={() => views(rl)} style={{ cursor: 'pointer' }} title="View RL" />
                            {(user?.user?.authorize === roleEnum.admin || hasActionPermission(screenEnum.rough_listing, screenActionEnum.update) || hasActionPermission(screenEnum.rough_listing, screenActionEnum.create)) && (
                                <EditIcon
                                    onClick={() => navigate(routeConstant.admin.roughListing.addrl.path, { state: { editData: rl } })}
                                    style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                                    title="Edit Rough Listing"
                                />
                            )}
                            <DownloadIcon onClick={() => handleDownloadIconClick(rl)} style={{ cursor: 'pointer', width: '20px', height: '20px' }} title="Download RL" className={`${theme}SvgIcon`} />
                        </div>
                    );
                }
            }
        ],
        [theme, user, showDownloadModal]
    );

    return (
        <DashboardWrapper>
            <div className={classNames(styles.UserContainer, 'text-start')}>
                <PoSearchBar setsearchMenu={setsearchMenu} searchMenu={searchMenu} suggestionsSource={PoList} SearchBars={SearchBars} />
                <div className={styles.Header}>
                    <div className="d-flex align-items-center gap-3 flex-wrap">
                        <Heading heading={'Rough Listing'} headingStyle={styles.Heading} />
                        <div className={styles.ViewToggleContainer}>
                            <button
                                type="button"
                                className={classNames(styles.ViewToggleBtn, viewMode === 'tree' && styles.ViewToggleBtnActive)}
                                onClick={() => handleViewModeChange('tree')}
                                title="Tree View: Grouped by Rough Listing & Batches"
                            >
                                <span>🌲 Tree View</span>
                                <span className={styles.ToggleCountBadge}>{searchResults?.length || 0}</span>
                            </button>
                            <button
                                type="button"
                                className={classNames(styles.ViewToggleBtn, viewMode === 'sku' && styles.ViewToggleBtnActive)}
                                onClick={() => handleViewModeChange('sku')}
                                title="SKU View: Flat list of all individual SKUs"
                            >
                                <span>📋 All SKUs</span>
                                <span className={styles.ToggleCountBadge}>{skuData?.length || 0}</span>
                            </button>
                        </div>
                    </div>
                    <div className={styles.HeaderActions}>
                        <Select
                            placeholder="Filter by Status"
                            isClearable
                            options={statusOptions}
                            value={selectedStatus}
                            menuPortalTarget={document.body}
                            styles={{
                                control: (provided) => ({
                                    ...provided,
                                    minWidth: 170,
                                    height: '40px',
                                    minHeight: '40px',
                                    borderRadius: '6px',
                                }),
                                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            }}
                            onChange={(item) => setSelectedStatus(item)}
                        />
                        <Select
                            placeholder="Filter by Warehouse"
                            isClearable
                            options={wareHouses}
                            value={selectedWarehouse}
                            menuPortalTarget={document.body}
                            styles={{
                                control: (provided) => ({
                                    ...provided,
                                    minWidth: 200,
                                    height: '40px',
                                    minHeight: '40px',
                                    borderRadius: '6px',
                                }),
                                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            }}
                            onChange={(item) => setSelectedWarehouse(item)}
                        />
                        <CustomButton
                            Icon={PlusIcon}
                            submitHandle={() => {
                                if (isRestrictActions(screenEnum.rough_listing, screenActionEnum.create)) {
                                    navigateToPath();
                                }
                            }}
                            IconStyle={classNames('me-1', styles.IconStyle)}
                            text={'Rough Listing'}
                            containerStyle={classNames(styles.AddProduct)}
                        />
                        <ExcelExport data={PoList} fileName={'Rough Listing'} />
                    </div>
                </div>
                {viewMode === 'tree' ? (
                    <GenericTable
                        key="tree_table"
                        tableName="rough_listing_tree_table_v3"
                        columns={columns}
                        data={searchResults || []}
                        renderRowSubComponent={renderRowSubComponent}
                        TableWrapperStyle={classNames(styles.Table)}
                    />
                ) : (
                    <GenericTable
                        key="sku_table"
                        tableName="rough_listing_sku_table_v3"
                        columns={skuColumns}
                        data={skuData || []}
                        TableWrapperStyle={classNames(styles.Table)}
                    />
                )}
                <AddClient show={openProduct} handleClose={closeProductHandler} />
            </div>
            {isLoading && <OverlayLoading />}
            {isGeneratingPdf && <OverlayLoading />}

            {/* Hidden container to perfectly render LayoutPo for PDF generation */}
            <div style={{ position: 'absolute', top: '-10000px', left: '-10000px', opacity: 0, pointerEvents: 'none' }}>
                <div ref={pdfContainerRef} style={{ width: '1000px', padding: '20px', backgroundColor: 'white' }}>
                    {selectedPO && <LayoutRl poDataProp={selectedPO} hidePrintButton={true} />}
                </div>
            </div>

            <Modal show={showDownloadModal} onHide={() => setShowDownloadModal(false)} centered size="sm">
                <Modal.Header closeButton className={classNames(styles.Header, `${theme}ModalBackground`)} style={{ borderBottom: 'none' }}>
                    <Heading heading={'Download Format'} headingStyle={styles.ModalHeading} />
                </Modal.Header>
                <Modal.Body className={classNames(`${theme}ModalBackground`, 'text-center py-4')}>
                    <p className="mb-4 text-muted" style={{ fontSize: '14px' }}>Select the format you want to download</p>
                    <div className="d-flex justify-content-center gap-3">
                        <CustomButton
                            text={'Excel (.xlsx)'}
                            containerStyle={styles.DownloadBtnExcel}
                            submitHandle={handleDownloadExcel}
                        />
                        <button 
                            className={classNames('btn btn-danger d-flex align-items-center justify-content-center', styles.DownloadBtnPdf)} 
                            onClick={handleDownloadPDF}
                            disabled={isGeneratingPdf}
                        >
                            {isGeneratingPdf ? 'Processing...' : 'PDF (.pdf)'}
                        </button>
                    </div>
                </Modal.Body>
            </Modal>
        </DashboardWrapper>
    );
}
